import { readJsonPath, fillPath } from '../predicate.js';
import {
  renderRosterActionButton,
  renderRosterBulkActionButton,
  renderDispositionFollowUp,
  renderRosterInlineForm,
  actionVisible,
  type ActionFetch,
  defaultActionFetch as defaultRosterActionFetch,
} from './roster-actions.js';
import type { RosterAction } from './roster-action-types.js';
import {
  buildFilterTabs,
  rowMatchesFilter,
  canNarrow,
  filterTabValue,
  rosterColumnSecondary,
  rosterColumnValue,
  resolveValue,
  stackSecondary,
  type RosterFilter,
  type RosterColumnSpec,
  type RosterColumnValueSource,
} from './roster-page.js';

/**
 * CAOS-382 — the generic RENDERER for an authored `rosterDetailPage` component
 * (kernel/src/meta/metatypes.ts). The single-record counterpart to {@link ../surfaces/roster-page.js
 * renderRosterPage}: same control-plane-backed browse, same fetch/navigate seam, drawing the SAME
 * shared record-surface pieces (`caos-breadcrumb`, `caos-record-header`, `caos-detail-section`,
 * `caos-key-value-row`, `caos-data-table`) every hand-written Setup detail pane already called by hand.
 *
 * App-agnostic — nothing here mentions Setup. Any package can author a `rosterDetailPage` and get a
 * real, working record surface with zero host code of its own.
 *
 * CAOS-337/CAOS-743 — a detail page can now ACT, not just browse: `actions` draws buttons into
 * `caos-record-header`'s own `actions` slot, and a table may declare its own alternate `read` source
 * (e.g. Login History reading `sessions/history` on a page whose primary `read` is
 * `permissions/resolve`) plus a per-row `rowAction` (e.g. "Unassign") drawn as a trailing button
 * column — see `roster-actions.ts` for the shared confirm/form-dialog renderer both draw from.
 *
 * CAOS-557 — two more additions, both generic (nothing here mentions Setup or seats):
 *   1. SINGLETON mode. `idParam` (and, with it, `breadcrumbRootLabel`/`breadcrumbRootHref`/
 *      `headingAt`/`notFoundTitle`/`notFoundBody`) is now OPTIONAL. A page that omits `idParam` has
 *      no addressed record — its `read` is called with no id parameter (just any static `params`),
 *      renders no breadcrumb, and its heading falls back to `spec.label`. There is no not-found
 *      state for a singleton (the id concept it would apply to does not exist); a failed read
 *      instead renders a generic "could not load" pane. `id` passed to {@link renderRosterDetailPage}
 *      is ignored in this mode.
 *   2. `selectable`/`bulkAction` on a `RosterTableSpec`: turns on `caos-data-table`'s own (already
 *      built-in) selection checkboxes and draws a `bulkAction` button that runs the SAME
 *      single-item verb a `rowAction` would, once per currently-selected row id — see
 *      `roster-actions.ts`'s `renderRosterBulkActionButton`/`runRosterBulkAction` for the mechanism;
 *      nothing about selection or batching is reinvented here.
 */

export type Navigate = (href: string) => void;

export interface RosterTableSpec {
  readonly heading: string;
  readonly rowsAt: string;
  /**
   * CAOS-1646 — the SAME {@link RosterColumnSpec} a roster's own columns are, which these have
   * always reused on the metatype. So a column here may project or fold its value too; threading
   * it was not optional, because leaving it out would have made `valueAt` optional on the schema
   * and unreadable on half the surfaces that take it.
   */
  readonly columns: readonly RosterColumnSpec[];
  readonly emptyTitle: string;
  readonly emptyBody: string;
  /** CAOS-337 — an alternate control-plane read route this table sources from; absent ⇒ reads `rowsAt` out of the page's primary payload. */
  readonly read?: { readonly noun: string; readonly verb: string };
  /** The query-parameter name the page's addressed id is sent under to `read`. */
  readonly idParam?: string;
  /** JSON path (within a row) to that row's own stable id — required for `rowAction`/`bulkAction`. */
  readonly rowIdField?: string;
  /** CAOS-337/CAOS-743 — a per-row action (e.g. "Unassign"), drawn as a trailing button column. */
  readonly rowAction?: RosterAction;
  /**
   * CAOS-699 — several per-row actions in one trailing column, each filtered per row by its own
   * `visibleWhen` and disabled per row by its own `enabledWhen`. Not together with `rowAction`.
   */
  readonly rowActions?: readonly RosterAction[];
  /** CAOS-699 — a form-kind action drawn under this (flat) table, e.g. an "Add value" row. */
  readonly inlineForm?: Extract<RosterAction, { readonly kind: 'form' }>;
  /**
   * CAOS-544 — segmented filter tabs above THIS table, drawn by the roster's own `buildFilterTabs`
   * and tested by its own `rowMatchesFilter`, so a table's tabs and a roster's tabs are one
   * behaviour rather than two that resemble each other. Requires `filterField`.
   */
  readonly filters?: readonly RosterFilter[];
  /** JSON path (within a row) the tabs test. Meaningless without `filters`. */
  readonly filterField?: string;
  /**
   * CAOS-544 — a query parameter of this table's own `read` whose value the READER chooses.
   *
   * Until a value is chosen the table does not fetch and shows its empty state: a comparison
   * against nobody is not an empty comparison, and drawing one would be inventing a result.
   */
  readonly parameter?: {
    readonly key: string;
    readonly label: string;
    readonly placeholder: string;
    readonly optionsFrom: {
      readonly read: { readonly noun: string; readonly verb: string };
      readonly rowsAt?: string;
      readonly valueAt: string;
      readonly labelAt: string;
    };
  };
  /** CAOS-557 — turns on `caos-data-table`'s own selection checkboxes. Requires `rowIdField`; meaningless without `bulkAction`. */
  readonly selectable?: boolean;
  /** CAOS-557 — a confirm-kind action run once per currently-selected row id (see the module doc). Requires `selectable: true` and `rowIdField`. */
  readonly bulkAction?: Extract<RosterAction, { readonly kind: 'confirm' }>;
  /**
   * CAOS-785 — a client-side navigation template for this table's PRIMARY column (its first
   * declared column), filled from the row exactly as {@link RosterPageSpec.detailRoute} already
   * fills from a roster's row (`{token}` → `readJsonPath(row, token)`, `fillPath` in `predicate.ts`).
   * Absent ⇒ the primary column renders as plain text, as every table did before this. This is
   * NOT the same axis as `rowAction`/`bulkAction` (a button that POSTs a write) — it is a plain
   * link that NAVIGATES, the one thing a `RosterAction` cannot do (it always names a `verb` to
   * write). Object Manager's per-object "Fields" table is the first user: each field row links to
   * its own nested Field-Level Security editor (`/setup/objects/{object}/fields/{apiName}`).
   */
  readonly rowHref?: string;
  /**
   * CAOS-786 — turns this table into a REPEATED table: a JSON path (within the PAGE payload) to
   * an array of GROUPS, each rendered as its own titled `caos-detail-section` with its own table,
   * for data whose grouping is real but whose group COUNT is not known at author time (Page
   * Layouts' sections — an object's layout can declare any number of them). Present ⇒ `rowsAt` is
   * read WITHIN EACH GROUP item, not the page payload. Absent ⇒ today's flat-table behaviour.
   */
  readonly groupsAt?: string;
  /** JSON path within a group item to its heading text; absent ⇒ "Section 1", "Section 2", … */
  readonly groupHeadingAt?: string;
  /** A group whose row array is empty shows this title; absent ⇒ falls back to `emptyTitle`. */
  readonly groupEmptyTitle?: string;
  /** A group whose row array is empty shows this body; absent ⇒ falls back to `emptyBody`. */
  readonly groupEmptyBody?: string;
}

export interface RosterDetailPageSpec {
  readonly key: string;
  readonly label: string;
  /** Required together with `idParam` (a per-record detail page's link back to its list). Absent in SINGLETON mode — see the module doc. */
  readonly breadcrumbRootLabel?: string;
  readonly breadcrumbRootHref?: string;
  readonly read: { readonly noun: string; readonly verb: string };
  /** Absent ⇒ SINGLETON mode (CAOS-557): no addressed record, `read` is called with no id param. Present ⇒ the query-parameter name the addressed id is sent under. */
  readonly idParam?: string;
  /** CAOS-748 — a fixed set of extra query params merged into every fetch of `read` (e.g. `{ kind: 'group' }` to disambiguate a `permissions/get` key shared by a set and a group). Absent ⇒ none. */
  readonly params?: Readonly<Record<string, string>>;
  /** Absent ⇒ the heading falls back to `label` (always the case in SINGLETON mode — there is no per-record value to read). */
  readonly headingAt?: string;
  /**
   * CAOS-1764 — a labelled value carries the SAME source keys a roster column does
   * ({@link RosterColumnValueSource}: `valueAt`, `derive`, `cases`/`otherwise`, `format`), so a
   * detail page can show the derived reference, folded Type/Status and formatted date the roster it
   * opens from already shows. `valueAt` is optional here because a row may fold `cases` instead;
   * which combinations are legal is the kernel's refusal, not this type's.
   */
  readonly kvRows: readonly ({ readonly label: string } & RosterColumnValueSource)[];
  readonly tables?: readonly RosterTableSpec[];
  /** CAOS-1912 — the shipped pieces this record places, for the part of a record that is neither a labelled value nor a table. */
  readonly pieces?: readonly RosterDetailPiece[];
  /** Required together with `idParam` — a SINGLETON page's read has no id to fail to resolve, so it has no not-found state (a failed read instead renders a generic load-error pane). */
  readonly notFoundTitle?: string;
  readonly notFoundBody?: string;
  /** CAOS-337/CAOS-743 — the buttons this record carries in `caos-record-header`'s `actions` slot. */
  readonly actions?: readonly RosterAction[];
}

/**
 * CAOS-1912 — one placed piece, exactly as it is authored.
 *
 * `key` and not `tag`: this is the stored body, and the stored body names a COMPONENT KEY. The
 * element to create is the `tag` that component's descriptor declares, and the two are not
 * interchangeable — a mismatch draws "code missing" over code that loaded perfectly. The lookup is
 * `ctx.componentTag`, so this type stays the authored shape and the renderer never invents a tag
 * from a key.
 */
export interface RosterDetailPiece {
  readonly id: string;
  /** The component this places, by key. The tag comes from `ctx.componentTag`. */
  readonly key: string;
  /** Absent ⇒ the piece is appended bare, with no section band around it. */
  readonly heading?: string;
  readonly inputs?: Readonly<Record<string, unknown>>;
  /** The input name the addressed record id is delivered to. Absent ⇒ the piece is told no id. */
  readonly idInput?: string;
}

export interface RosterFetchResponse {
  json(): Promise<unknown>;
}
export type RosterFetch = (path: string) => Promise<RosterFetchResponse>;

export const defaultRosterFetch: RosterFetch = (path) =>
  fetch(path, { credentials: 'same-origin' }) as unknown as Promise<RosterFetchResponse>;

interface Envelope {
  ok?: boolean;
  data?: unknown;
  error?: { message?: string };
}

/**
 * Build the `read` fetch path: `idParam=id` (CAOS-557: only when the page declares an `idParam` —
 * a SINGLETON page has none), then any declared static `params` (CAOS-748), in order. A page with
 * neither carries no query string at all (e.g. `seats/capacity`, which needs none).
 */
function buildReadPath(
  read: { noun: string; verb: string },
  idParam: string | undefined,
  id: string | undefined,
  params?: Readonly<Record<string, string>>,
): string {
  const pairs: [string, string][] = [];
  if (idParam !== undefined && id !== undefined) pairs.push([idParam, id]);
  pairs.push(...Object.entries(params ?? {}));
  if (pairs.length === 0) return `/api/${read.noun}/${read.verb}`;
  const qs = pairs.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
  return `/api/${read.noun}/${read.verb}?${qs}`;
}

async function fetchRecord(spec: RosterDetailPageSpec, id: string | undefined, doFetch: RosterFetch): Promise<unknown | null> {
  let env: Envelope;
  try {
    const path = buildReadPath(spec.read, spec.idParam, id, spec.params);
    const res = await doFetch(path);
    env = (await res.json()) as Envelope;
  } catch {
    return null;
  }
  if (env?.ok !== true) return null;
  return env.data ?? null;
}

/**
 * CAOS-1848 — one page of a table's rows, and whether the route said there are more.
 *
 * The cursor is the half that was missing, and its absence was not a cosmetic gap. A table reading
 * a PAGED control-plane route got the route's first page and nothing else: this function made one
 * fetch, sent no `limit` and no `cursor`, and dropped the `nextCursor` the payload carried. For
 * `data query` — the route a per-object records browser has to read — the first page is **50
 * records** (`api/data-routes.ts`'s `queryHandler`, which defaults `limit` to 50 and caps it at
 * 200). So a records table over an object holding fifty thousand rows drew fifty of them, with
 * nothing on the screen saying so, and a filter tab above it narrowed those fifty while appearing
 * to narrow the object.
 *
 * `cursor` in and `nextCursor` out is the kernel's own convention rather than a shape invented
 * here: ten route modules under `kernel/src/api` answer with `nextCursor`, and every one of them
 * that takes a cursor takes it as `cursor` (`assignment-rule`, `auth` ×2, `data`, `observability`).
 * A read that carries no `nextCursor` is unchanged by this — `more` comes back `undefined` and the
 * table behaves exactly as it did.
 */
interface TableRowPage {
  readonly rows: Record<string, unknown>[];
  /** The cursor the route handed back, or `undefined` when it said this is the whole list. */
  readonly more: string | undefined;
}

/**
 * The rows at `rowsAt`, filtered to real objects. Shared by the paged read and the inline payload.
 *
 * `rowsAt` is `string`, not `string | undefined`, and that is load-bearing rather than tidiness:
 * `readJsonPath` does `path.split('.')`, so an absent path is a TypeError at the first row read,
 * not an empty table. `RosterTableSpec.rowsAt` is required on the metatype, so no caller has one to
 * give — and widening the parameter to accept it anyway is how that crash gets a way in.
 */
function rowsOf(source: unknown, rowsAt: string): Record<string, unknown>[] {
  const raw = readJsonPath(source, rowsAt);
  return Array.isArray(raw) ? raw.filter((r): r is Record<string, unknown> => r != null && typeof r === 'object') : [];
}

/**
 * The `nextCursor` SIBLING of the rows array, which is where every paged route puts it.
 *
 * Addressed as a sibling rather than at the payload root because `rowsAt` may be nested, and the
 * root is the wrong object in that case. A non-string, or the `null` a route sends to mean "that
 * was the last page", both read as no cursor — the distinction a caller needs is "is there more",
 * and only a usable string is.
 */
function cursorBeside(source: unknown, rowsAt: string): string | undefined {
  const dot = rowsAt.lastIndexOf('.');
  const container = dot === -1 ? source : readJsonPath(source, rowsAt.slice(0, dot));
  if (container == null || typeof container !== 'object') return undefined;
  const next = (container as Record<string, unknown>)['nextCursor'];
  return typeof next === 'string' && next !== '' ? next : undefined;
}

/**
 * Read a table's own row array — either out of the page's primary payload, or its own alternate
 * `read` route. CAOS-557: on a SINGLETON page (`id` is `undefined`) an alternate route is called
 * with no id parameter at all — e.g. Seat Allocation's "People" table reads `permissions/list`
 * plain, exactly the same call `users_list.json` already makes unmodified.
 *
 * `cursor` (CAOS-1848) continues a paged read. It is only ever a value this function itself
 * received from the same route, never one the author wrote, so a route that answers with no
 * `nextCursor` is never sent one.
 */
async function fetchTableRows(
  tableSpec: RosterTableSpec,
  primaryPayload: unknown,
  id: string | undefined,
  doFetch: RosterFetch,
  chosen?: string,
  cursor?: string,
): Promise<TableRowPage> {
  let source: unknown = primaryPayload;
  if (tableSpec.read) {
    try {
      const idParam = tableSpec.idParam ?? 'id';
      const pairs: [string, string][] = [];
      if (id !== undefined) pairs.push([idParam, id]);
      // CAOS-544 — the reader's own choice, sent under the name the table declared for it.
      if (tableSpec.parameter && chosen !== undefined) pairs.push([tableSpec.parameter.key, chosen]);
      if (cursor !== undefined) pairs.push(['cursor', cursor]);
      const qs = pairs.map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join('&');
      const path = qs === '' ? `/api/${tableSpec.read.noun}/${tableSpec.read.verb}` : `/api/${tableSpec.read.noun}/${tableSpec.read.verb}?${qs}`;
      const res = await doFetch(path);
      const env = (await res.json()) as Envelope;
      source = env?.ok === true ? env.data : null;
    } catch {
      source = null;
    }
  }
  // A table drawn from the page's PRIMARY payload is not a paged read — it is a slice of a record
  // the page already fetched whole — so no cursor is reported for it even if the record's payload
  // happens to carry one for some unrelated collection.
  return { rows: rowsOf(source, tableSpec.rowsAt), more: tableSpec.read ? cursorBeside(source, tableSpec.rowsAt) : undefined };
}

/** CAOS-557: takes the root label/href directly (not the whole spec) so a caller only builds one when both are actually present — SINGLETON pages have neither and render no breadcrumb at all. */
function breadcrumb(rootLabel: string, rootHref: string, label: string, navigate?: Navigate): HTMLElement {
  const crumbs = document.createElement('caos-breadcrumb');
  (crumbs as unknown as { items: unknown }).items = [{ label: rootLabel, href: rootHref }, { label }];
  crumbs.addEventListener('click', (ev) => {
    const anchor = (ev.target as HTMLElement | null)?.closest('a');
    const href = anchor?.getAttribute('href');
    if (href && href.startsWith('/')) {
      ev.preventDefault();
      navigate?.(href);
    }
  });
  return crumbs;
}

function kvRow(label: string, value: string): HTMLElement {
  const row = document.createElement('caos-key-value-row');
  row.setAttribute('label', label);
  row.setAttribute('stacked', '');
  row.setAttribute('no-border', '');
  if (value) row.setAttribute('value', value);
  return row;
}

function cellValue(text: string, chip: boolean | undefined): unknown {
  if (!chip) return text;
  const el = document.createElement('caos-tag');
  el.textContent = text;
  return { node: el };
}

/** CAOS-785 — an in-app link that routes through the navigate seam, mirroring `roster-page.ts`'s own. */
function rowLink(text: string, href: string, navigate?: Navigate): HTMLAnchorElement {
  const a = document.createElement('a');
  a.className = 'setup-link';
  a.href = href;
  a.textContent = text;
  a.addEventListener('click', (ev) => {
    if (ev.defaultPrevented || ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    ev.preventDefault();
    navigate?.(href);
  });
  return a;
}

/** What a table needs from the page it is drawn on. */
interface TableContext {
  readonly navigate?: Navigate;
  readonly actionFetch: ActionFetch;
  readonly redraw: () => void;
  /** CAOS-699 — where a row action's refusal is shown (the page's error banner). */
  readonly onError?: (message: string) => void;
}

/**
 * CAOS-786 — build one wired `caos-data-table` from already-resolved rows: the shared piece
 * between a flat table (today's only shape) and each group's own table under `groupsAt`. Handles
 * `rowHref`/`rowAction`, exactly as the single call site did before this was extracted.
 */
function buildTableElement(
  tableSpec: RosterTableSpec,
  tableRows: readonly Record<string, unknown>[],
  recordId: string,
  ctx: TableContext,
  /**
   * CAOS-142 — the heading of the group these rows came from, when this table is one group of a
   * `groupsAt` table. Passed to every row action so a `"{groupHeading}"` token can be filled: a
   * layout verb names a section by its LABEL, and the label belongs to the group while the row
   * carries only the field. The RAW resolved heading, deliberately — not the `Section N` display
   * fallback, which names no section that exists and would send the verb looking for one.
   */
  groupHeading?: string,
  /**
   * CAOS-142 — the page payload, handed to every row action so `paramsFrom` can read a value the
   * address cannot carry. Separate from the row: the row is one of `rowsAt`'s items, and the thing
   * a layout verb needs (the layout's key) is a property of the page.
   */
  payload?: unknown,
): HTMLElement {
  const t = document.createElement('caos-data-table');
  t.setAttribute('label', tableSpec.heading);
  t.setAttribute('empty-label', tableSpec.emptyBody);
  // CAOS-557 — turns on caos-data-table's OWN built-in selection checkboxes (`selectable`,
  // `row.selected`, `selectedIds`) rather than reimplementing any of that here.
  if (tableSpec.selectable) (t as unknown as { selectable: boolean }).selectable = true;
  const columns = tableSpec.columns.map((c) => ({ key: c.key, label: c.label }));
  if (tableSpec.rowAction) columns.push({ key: '__rosterRowAction', label: '' });
  if (tableSpec.rowActions) columns.push({ key: '__rosterRowActions', label: '' });
  (t as unknown as { columns: unknown }).columns = columns;
  (t as unknown as { rows: unknown }).rows = tableRows.map((row, i) => {
    const cells: Record<string, unknown> = Object.fromEntries(
      // CAOS-1646 — through the shared resolver, so a projected or folded value renders here the
      // same way it does on a roster's own table.
      // CAOS-1717 — through the SAME stacking helper the roster's own table uses. These tables
      // reuse `RosterColumnSpec`, so admitting `secondary` on the schema admitted it here; a
      // `secondary` authored on a child-table column and silently not drawn would be precisely the
      // "the author states an intent, nothing reads it, and nothing says so" defect this ticket was
      // filed about, recreated one surface over.
      tableSpec.columns.map((c) => [
        c.key,
        stackSecondary(cellValue(rosterColumnValue(row, c), c.chip), rosterColumnSecondary(row, c)),
      ]),
    );
    // CAOS-785 — when the table declares `rowHref`, its PRIMARY (first) column becomes a link
    // instead of plain text, filled from this row exactly as a roster's `detailRoute` already
    // fills from a roster row.
    const primary = tableSpec.columns[0];
    if (tableSpec.rowHref && primary) {
      const { path, complete } = fillPath(tableSpec.rowHref, row);
      if (complete) {
        // The link's TEXT is the same value the cell would otherwise show, so a primary column
        // that projects or folds reads identically whether or not `rowHref` made it a link.
        cells[primary.key] = stackSecondary(
          { node: rowLink(rosterColumnValue(row, primary), path, ctx.navigate) },
          rosterColumnSecondary(row, primary),
        );
      }
    }
    if (tableSpec.rowAction) {
      const rowId = tableSpec.rowIdField ? String(readJsonPath(row, tableSpec.rowIdField) ?? '') : '';
      const btn = renderRosterActionButton({
        action: tableSpec.rowAction,
        record: row,
        recordId,
        rowId,
        groupHeading,
        payload,
        fetch: ctx.actionFetch,
        onSuccess: () => ctx.redraw(),
      });
      cells['__rosterRowAction'] = { node: btn };
    }
    if (tableSpec.rowActions) {
      const rowId = tableSpec.rowIdField ? String(readJsonPath(row, tableSpec.rowIdField) ?? '') : '';
      const group = document.createElement('span');
      group.className = 'roster-row-actions';
      for (const action of tableSpec.rowActions) {
        if (!actionVisible(action, row)) continue;
        group.append(
          renderRosterActionButton({
            action,
            record: row,
            recordId,
            rowId,
            groupHeading,
            payload,
            fetch: ctx.actionFetch,
            onSuccess: () => ctx.redraw(),
            onError: ctx.onError,
          }),
        );
      }
      cells['__rosterRowActions'] = { node: group };
    }
    return {
      id: String(readJsonPath(row, tableSpec.rowIdField ?? (tableSpec.columns[0]?.valueAt ?? '')) ?? i),
      cells,
    };
  });
  return t;
}

/**
 * CAOS-786 — render `tableSpec.groupsAt` as N titled sub-sections, one per group item, each with
 * its own table built from `rowsAt` read WITHIN that group (not the page payload). A group with no
 * rows shows its own (or the table's shared) empty state; the whole thing renders nothing when the
 * groups array itself resolves empty — the same "an absent thing draws nothing" posture every
 * other honest-empty-state pane in this module already takes.
 */
function renderGroupedTable(
  root: HTMLElement,
  tableSpec: RosterTableSpec,
  payload: unknown,
  recordId: string,
  ctx: TableContext,
): void {
  const raw = readJsonPath(payload, tableSpec.groupsAt!);
  const groups = Array.isArray(raw) ? raw.filter((g): g is Record<string, unknown> => g != null && typeof g === 'object') : [];

  groups.forEach((group, i) => {
    const section = document.createElement('caos-detail-section');
    const heading = tableSpec.groupHeadingAt ? String(readJsonPath(group, tableSpec.groupHeadingAt) ?? '') : '';
    section.setAttribute('heading', heading.length > 0 ? heading : `Section ${i + 1}`);

    const rawRows = readJsonPath(group, tableSpec.rowsAt);
    const rows = Array.isArray(rawRows) ? rawRows.filter((r): r is Record<string, unknown> => r != null && typeof r === 'object') : [];
    if (rows.length === 0) {
      const empty = document.createElement('caos-empty-state');
      empty.setAttribute('title', tableSpec.groupEmptyTitle ?? tableSpec.emptyTitle);
      empty.textContent = tableSpec.groupEmptyBody ?? tableSpec.emptyBody;
      section.append(empty);
    } else {
      // `heading`, NOT the `Section N` fallback the attribute above falls back to. That fallback is
      // a display label for a group whose heading did not resolve; sending it would have a verb
      // look for a section called "Section 1" and refuse, naming something the author never wrote.
      // An unresolved heading goes as empty and the route refuses the call for a missing parameter,
      // which is the truthful answer: the screen cannot say which group this row is in.
      section.append(buildTableElement(tableSpec, rows, recordId, ctx, heading, payload));
    }
    root.append(section);
  });
}

function emptyState(title: string, body: string): HTMLElement {
  const empty = document.createElement('caos-empty-state');
  empty.setAttribute('title', title);
  empty.textContent = body;
  return empty;
}

/**
 * CAOS-544 — the select a reader picks this table's own query-parameter value from.
 *
 * Options come from a control-plane read, the same `optionsFrom` shape a form field's select
 * already uses, so "which people can I compare against" is answered by the People inventory route
 * rather than by a list baked into the page. A failed options read leaves the select with only its
 * placeholder: the control is visibly there and visibly has nothing to offer, which is the honest
 * reading of a lookup that did not answer — quieter than an error and truer than a silent absence.
 */
async function buildParameterSelect(
  parameter: NonNullable<RosterTableSpec['parameter']>,
  doFetch: RosterFetch,
  onChange: (value: string | undefined) => void,
): Promise<HTMLElement> {
  const wrap = document.createElement('div');
  wrap.className = 'roster-table-parameter';
  const label = document.createElement('label');
  label.textContent = parameter.label;
  const select = document.createElement('select');
  label.append(select);

  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = parameter.placeholder;
  select.append(placeholder);

  let rows: Record<string, unknown>[] = [];
  try {
    const res = await doFetch(`/api/${parameter.optionsFrom.read.noun}/${parameter.optionsFrom.read.verb}`);
    const env = (await res.json()) as Envelope;
    const data = env?.ok === true ? env.data : null;
    const raw = parameter.optionsFrom.rowsAt !== undefined ? readJsonPath(data, parameter.optionsFrom.rowsAt) : data;
    rows = Array.isArray(raw) ? raw.filter((r): r is Record<string, unknown> => r != null && typeof r === 'object') : [];
  } catch {
    rows = [];
  }
  for (const row of rows) {
    const value = String(readJsonPath(row, parameter.optionsFrom.valueAt) ?? '');
    if (value === '') continue;
    const option = document.createElement('option');
    option.value = value;
    option.textContent = String(readJsonPath(row, parameter.optionsFrom.labelAt) ?? value);
    select.append(option);
  }

  select.addEventListener('change', () => onChange(select.value === '' ? undefined : select.value));
  wrap.append(label);
  return wrap;
}

/**
 * Render ONE flat table's section: its controls (CAOS-544's filter tabs and reader-chosen
 * parameter), then the rows those controls select, then any inline form.
 *
 * The controls own their state and redraw only the rows beneath them. Routing them through the
 * page's `redraw` instead would re-fetch the whole record and reset the reader's choice on every
 * click — the control would appear to work and then undo itself, which is worse than not offering
 * it at all.
 */
async function renderTableSection(
  tableSpec: RosterTableSpec,
  payload: unknown,
  id: string | undefined,
  recordId: string,
  ctx: TableContext & { readonly fetch: RosterFetch },
): Promise<HTMLElement> {
  const section = document.createElement('caos-detail-section');
  section.setAttribute('heading', tableSpec.heading);

  let activeFilter = 0;
  let chosen: string | undefined;

  /**
   * CAOS-1848 — the pages read so far, and the cursor onto the next one.
   *
   * Held out here rather than inside `drawRows` because continuing a read must not re-fetch what is
   * already on screen. `loaded` is the union of every page fetched for the CURRENT request.
   *
   * WHICH CHANGES RESET IT, which is not the same answer for the two controls above and is worth
   * being exact about. The reader's `parameter` is sent to the route (`tableSpec.parameter.key`),
   * so changing it asks a different question and the pages gathered under the old one are not part
   * of the answer — reset. A FILTER TAB narrows client-side over rows already in hand and sends
   * nothing, so changing it must NOT reset: doing so would discard every page past the first and
   * silently re-shrink the list the reader had just extended.
   */
  let loaded: Record<string, unknown>[] = [];
  let cursor: string | undefined;
  let exhausted = false;
  /**
   * Whether a page has been fetched at all for the current request — tracked separately from
   * `loaded.length`, because those two are not the same question.
   *
   * A route may legitimately answer an EMPTY first page with a live `nextCursor` (`data query` over
   * an object whose first page falls in a gap the read skips). That leaves `loaded` empty and
   * `exhausted` false, so a guard reading `loaded.length === 0` treats every later redraw — a
   * filter-tab click, which deliberately does not reset — as "nothing read yet" and re-fetches page
   * one, discarding the cursor it was already holding. The read never advances and the fetch
   * repeats on every tab press.
   */
  let havePage = false;

  const resetPaging = (): void => {
    loaded = [];
    cursor = undefined;
    exhausted = false;
    havePage = false;
  };

  const rowsHost = document.createElement('div');
  rowsHost.className = 'roster-table-rows';

  const drawRows = async (): Promise<void> => {
    rowsHost.replaceChildren();
    // A table waiting on a reader's choice has not been asked its question yet. It draws its empty
    // state rather than the answer to a question nobody asked.
    if (tableSpec.parameter !== undefined && chosen === undefined) {
      rowsHost.append(emptyState(tableSpec.emptyTitle, tableSpec.emptyBody));
      return;
    }
    if (!havePage) {
      const first = await fetchTableRows(tableSpec, payload, id, ctx.fetch, chosen);
      loaded = first.rows;
      cursor = first.more;
      exhausted = first.more === undefined;
      havePage = true;
    }
    const fetched = loaded;
    // CAOS-1646 R2 — the same active-tab gate and the same value resolution the roster uses, so a
    // table's tabs and a roster's tabs stay one behaviour rather than two that resemble each other.
    const tableRows = canNarrow(tableSpec.filters, activeFilter, tableSpec.filterField)
      ? fetched.filter((r) =>
          rowMatchesFilter(
            tableSpec.filters![activeFilter]!,
            filterTabValue(r, tableSpec.filters![activeFilter]!, tableSpec.columns, tableSpec.filterField),
          ),
        )
      : fetched;

    if (tableRows.length === 0) {
      rowsHost.append(emptyState(tableSpec.emptyTitle, tableSpec.emptyBody));
      // A page of rows that the active FILTER left empty is still a partial read, and the next page
      // may hold matches. Saying nothing here is how a tab reads as "none of these exist" when what
      // it means is "none in the first fifty" — so the control is offered under the empty state too.
      if (!exhausted) rowsHost.append(continuation());
      return;
    }
    const t = buildTableElement(tableSpec, tableRows, recordId, ctx, undefined, payload);

    // CAOS-557 — the bulkAction toolbar, fanning one verb over caos-data-table's OWN live
    // `selectedIds` and enabled only while at least one row is checked.
    if (tableSpec.bulkAction) {
      const toolbar = document.createElement('div');
      toolbar.className = 'roster-bulk-toolbar';
      const bulkBtn = renderRosterBulkActionButton({
        action: tableSpec.bulkAction,
        recordId,
        getSelectedIds: () => (t as unknown as { selectedIds: string[] }).selectedIds,
        payload,
        fetch: ctx.actionFetch,
        onSuccess: () => ctx.redraw(),
      });
      const syncEnabled = (): void => {
        bulkBtn.disabled = (t as unknown as { selectedIds: string[] }).selectedIds.length === 0;
      };
      syncEnabled();
      t.addEventListener('caos-row-select', syncEnabled);
      t.addEventListener('caos-select-all', syncEnabled);
      toolbar.append(bulkBtn);
      rowsHost.append(toolbar);
    }
    rowsHost.append(t);
    if (!exhausted) rowsHost.append(continuation());
  };

  /**
   * CAOS-1848 — the control that continues a paged read, and the sentence that stops the rows
   * above it reading as the whole list.
   *
   * The sentence is the load-bearing half. A "Show more" button alone still leaves a reader who
   * does not press it believing they have seen everything, and it leaves anybody copying the table
   * out believing the same. So the count on screen is stated, and it is stated as a floor.
   *
   * It REFUSES TO LOOP. If a route answers a cursor with the same cursor, or with no rows at all,
   * this stops and says the read went no further rather than offering the button again — a route
   * that advertises `nextCursor` and ignores `cursor` would otherwise spin forever on one page.
   * Six of the ten route modules answering with `nextCursor` were not measured to consume one, so
   * this is the case that is likely rather than theoretical.
   *
   * NOT HANDLED, and named here rather than left to be rediscovered: continuing a read calls
   * `drawRows`, which replaces the `caos-data-table` with a new one, so a `bulkAction` table's
   * CHECKED ROWS are discarded by pressing "Show more". No shipped table hits this — every current
   * `bulkAction` table reads an unpaged route (`seats/reclaim`, `permissions/list`) — but the first
   * paged records browser with a bulk verb on it will, and the checkboxes will clear with nothing on
   * screen saying why. Carrying selection across the rebuild is CAOS-557's surface, not this one.
   */
  function continuation(): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'roster-table-more';

    const note = document.createElement('p');
    note.className = 'roster-table-more-note';
    note.textContent = `Showing ${loaded.length} so far. There are more to read.`;
    wrap.append(note);

    const btn = document.createElement('caos-button');
    btn.textContent = 'Show more';
    btn.addEventListener('click', () => {
      void (async () => {
        const asked = cursor;
        btn.setAttribute('disabled', '');
        const next = await fetchTableRows(tableSpec, payload, id, ctx.fetch, chosen, asked);
        // No new rows, or the same cursor handed back: the read is not advancing. Stop rather than
        // redraw the same control, and say what happened instead of looking like nothing happened.
        if (next.rows.length === 0 || next.more === asked) {
          exhausted = true;
          note.textContent = `Showing ${loaded.length}. The list did not read any further.`;
          btn.remove();
          return;
        }
        loaded = [...loaded, ...next.rows];
        cursor = next.more;
        exhausted = next.more === undefined;
        await drawRows();
      })();
    });
    wrap.append(btn);
    return wrap;
  }

  if (tableSpec.parameter) {
    section.append(
      await buildParameterSelect(tableSpec.parameter, ctx.fetch, (value) => {
        chosen = value;
        // CAOS-1848 — a new choice is a new request, so the pages read under the old one go.
        resetPaging();
        void drawRows();
      }),
    );
  }
  if (tableSpec.filters && tableSpec.filters.some((_, i) => canNarrow(tableSpec.filters, i, tableSpec.filterField))) {
    const tabs = tableSpec.filters;
    // The bar is rebuilt on each change because `buildFilterTabs` renders the active tab into the
    // markup; a stable host element is what keeps it in place while its contents are replaced.
    const tabHost = document.createElement('div');
    const drawTabs = (): void => {
      tabHost.replaceChildren(
        buildFilterTabs(tabs, activeFilter, (i) => {
          activeFilter = i;
          drawTabs();
          void drawRows();
        }),
      );
    };
    drawTabs();
    section.append(tabHost);
  }

  section.append(rowsHost);
  await drawRows();

  // CAOS-699 — drawn whether or not the table has rows: an empty list is where adding starts.
  if (tableSpec.inlineForm) {
    section.append(
      renderRosterInlineForm({
        action: tableSpec.inlineForm,
        record: payload,
        recordId,
        fetch: ctx.actionFetch,
        onSuccess: () => ctx.redraw(),
      }),
    );
  }
  return section;
}

/** Per-record not-found (idParam mode only — a record was addressed and no such record exists). */
function renderNotFound(root: HTMLElement, spec: RosterDetailPageSpec, id: string, navigate?: Navigate): void {
  root.replaceChildren();
  if (spec.breadcrumbRootLabel !== undefined && spec.breadcrumbRootHref !== undefined) {
    root.append(breadcrumb(spec.breadcrumbRootLabel, spec.breadcrumbRootHref, id, navigate));
  }
  const empty = document.createElement('caos-empty-state');
  empty.setAttribute('title', spec.notFoundTitle ?? `${spec.label} not found`);
  empty.textContent = (spec.notFoundBody ?? `No "${id}" is defined in this org.`).replace('{id}', id);
  root.append(empty);
}

/**
 * CAOS-557 — SINGLETON mode's failed-read pane. There is no id to have failed to resolve (unlike
 * `renderNotFound`), so this names no id and asks nothing of `notFoundTitle`/`notFoundBody` (which
 * a singleton page does not declare).
 */
function renderLoadError(root: HTMLElement, spec: RosterDetailPageSpec, navigate?: Navigate): void {
  root.replaceChildren();
  if (spec.breadcrumbRootLabel !== undefined && spec.breadcrumbRootHref !== undefined) {
    root.append(breadcrumb(spec.breadcrumbRootLabel, spec.breadcrumbRootHref, spec.label, navigate));
  }
  const empty = document.createElement('caos-empty-state');
  empty.setAttribute('title', `Could not load ${spec.label}`);
  empty.textContent = 'This page could not be read right now. Try again in a moment.';
  root.append(empty);
}

async function renderRecord(
  root: HTMLElement,
  spec: RosterDetailPageSpec,
  id: string | undefined,
  payload: unknown,
  ctx: {
    readonly navigate?: Navigate;
    readonly fetch: RosterFetch;
    readonly actionFetch: ActionFetch;
    readonly redraw: () => void;
    readonly componentTag?: (key: string) => string | undefined;
  },
): Promise<void> {
  root.replaceChildren();
  // CAOS-557 — SINGLETON mode (no headingAt) falls back to the page's own label; there is no
  // per-record value to read a heading from.
  const heading = spec.headingAt !== undefined ? String(readJsonPath(payload, spec.headingAt) ?? '') : spec.label;
  if (spec.breadcrumbRootLabel !== undefined && spec.breadcrumbRootHref !== undefined) {
    root.append(breadcrumb(spec.breadcrumbRootLabel, spec.breadcrumbRootHref, heading, ctx.navigate));
  }

  const head = document.createElement('caos-record-header');
  head.setAttribute('object-label', spec.label);
  head.setAttribute('heading', heading);
  head.setAttribute('no-icon', '');

  // CAOS-557 — a SINGLETON page has no addressed record; `{recordId}` substitutes '' for it (a
  // safe no-op — seat_allocation.json, the first SINGLETON page, declares no `{recordId}` token
  // anywhere, only `{rowId}`/`{selectedId}`).
  const recordId = id ?? '';

  for (const action of spec.actions ?? []) {
    if (!actionVisible(action, payload)) continue;
    const btn = renderRosterActionButton({
      action,
      record: payload,
      recordId,
      payload,
      fetch: ctx.actionFetch,
      onSuccess: () => ctx.redraw(),
      onDispositionNeeded: (objectKeys, resubmit) =>
        renderDispositionFollowUp(objectKeys, resubmit, { fetch: ctx.actionFetch, onDone: () => ctx.redraw() }),
    });
    btn.setAttribute('slot', 'actions');
    head.append(btn);
  }
  root.append(head);

  // CAOS-699 — a row action has no dialog to report a refusal in, so the page says it here, under the
  // header, until the next successful write redraws the page.
  const banner = document.createElement('div');
  banner.className = 'roster-action-error roster-page-error';
  banner.setAttribute('role', 'alert');
  banner.hidden = true;
  root.append(banner);
  const tableCtx: TableContext = {
    navigate: ctx.navigate,
    actionFetch: ctx.actionFetch,
    redraw: ctx.redraw,
    onError: (message) => {
      banner.textContent = message;
      banner.hidden = false;
    },
  };

  const details = document.createElement('caos-detail-section');
  details.setAttribute('heading', 'Details');
  const grid = document.createElement('div');
  grid.className = 'setup-field-grid';
  for (const row of spec.kvRows) {
    // CAOS-1764 — through the SHARED resolver, which is the whole point of the widening: a labelled
    // row's derived reference, folded word and formatted date are computed by the same cascade as a
    // roster column's, so the list and the record it opens onto cannot come to say different things
    // about the same field. `resolveValue` was already lifted out for a column's `secondary`; this is
    // its third caller and it is unchanged by being one.
    grid.append(kvRow(row.label, resolveValue(payload, row)));
  }
  details.append(grid);
  root.append(details);

  for (const tableSpec of spec.tables ?? []) {
    // CAOS-786 — a REPEATED table renders as N titled sub-sections (one per group), not the one
    // section every other table draws; it reads its groups out of the PAGE payload directly, never
    // an alternate `read` (a grouped table always names the group source once, structurally).
    if (tableSpec.groupsAt) {
      renderGroupedTable(root, tableSpec, payload, recordId, tableCtx);
      continue;
    }

    root.append(await renderTableSection(tableSpec, payload, id, recordId, { ...tableCtx, fetch: ctx.fetch }));
  }

  // CAOS-1912 — the shipped pieces this record places, LAST and in authored order.
  //
  // Last because everything above is the record as the route answered it, read top to bottom:
  // the heading, its labelled values, then its child tables. A piece is a surface of its own and
  // the thing a reader scrolls to, not something to meet before the record it is about.
  for (const piece of spec.pieces ?? []) {
    root.append(renderPiece(piece, recordId, ctx.componentTag));
  }
}

/**
 * CAOS-1912 — one placed piece: the custom element the component registers, with this placement's
 * inputs on it.
 *
 * ── A KEY THAT RESOLVES TO NO TAG SAYS SO, IN WORDS ───────────────────────────────────────────
 * A placement naming a component the org does not have is not a crash and must not be silence
 * either: `document.createElement` of a key would make an unknown element, which draws an empty box
 * that is indistinguishable from a component whose code failed to load. The same two findings this
 * file's `renderNotFound` already separates everywhere else — install the package that provides it,
 * versus this address holds nothing — so the sentence names the key and says what to do.
 *
 * ── NO HEADING ELEMENT WHEN NONE IS AUTHORED ──────────────────────────────────────────────────
 * An empty `caos-detail-section` heading is a visible band of nothing. A piece with no heading is
 * appended bare, which is also what a `page` does with a component placed in a section that has
 * none.
 */
function renderPiece(
  piece: RosterDetailPiece,
  recordId: string,
  componentTag: ((key: string) => string | undefined) | undefined,
): HTMLElement {
  const tag = componentTag?.(piece.key);
  if (tag === undefined || tag === '') {
    const missing = document.createElement('caos-empty-state');
    missing.setAttribute('title', 'This piece is not installed');
    missing.setAttribute(
      'body',
      `This record page places a component called "${piece.key}", and this workspace does not have it. ` +
        'Install the package that provides it.',
    );
    return missing;
  }
  const el = document.createElement(tag);
  // The addressed id FIRST, so a placement that also names the same input in `inputs` overrides it
  // deliberately rather than by accident of ordering — an authored literal is a decision somebody
  // made, and the id is the default this page supplies.
  if (piece.idInput !== undefined) applyPieceInput(el, piece.idInput, recordId);
  for (const [name, value] of Object.entries(piece.inputs ?? {})) applyPieceInput(el, name, value);

  if (piece.heading === undefined) return el;
  const section = document.createElement('caos-detail-section');
  section.setAttribute('heading', piece.heading);
  section.append(el);
  return section;
}

/**
 * One input, delivered the way every other placement delivers one: a string or a number becomes a
 * kebab-case ATTRIBUTE, anything else becomes a property.
 *
 * Deliberately the same rule `page-surface.ts`'s `applyProps` uses, and deliberately NOT a call to
 * it: that function also takes a component's `propertyOnly` set, which a record page has no way to
 * read here. A component whose input is property-only would be mis-delivered by this, and the honest
 * answer is that no such component is placeable on a record page yet — when one is, this should call
 * the shared function with the declaration rather than grow a second copy of the rule.
 */
function applyPieceInput(el: HTMLElement, name: string, value: unknown): void {
  if (value === undefined || value === null) return;
  const attribute = name.replace(/([a-z0-9])([A-Z])/g, '$1-$2').replace(/_/g, '-').toLowerCase();
  if (typeof value === 'boolean') {
    if (value) el.setAttribute(attribute, '');
    else el.removeAttribute(attribute);
    return;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    el.setAttribute(attribute, String(value));
    return;
  }
  (el as unknown as Record<string, unknown>)[name] = value;
}

/**
 * Render an authored `rosterDetailPage`: fetch its read route (with `idParam=id` when the page
 * declares one — CAOS-557's SINGLETON mode calls it with none) and draw the breadcrumb (when
 * declared) + record header (+ any declared `actions`) + kv-rows detail section + any declared
 * child tables (each its own data source + optional `rowAction`/`selectable`+`bulkAction`), or a
 * failed-read pane (a declared not-found pane for a missing record; a generic load-error pane for
 * a SINGLETON page, which has no id to have failed to resolve). Returns `{ root, done }` so a test
 * can await the initial fetch before asserting.
 *
 * `id` is omitted (or ignored if passed) for a SINGLETON page — one with no `idParam`.
 */
export function renderRosterDetailPage(
  spec: RosterDetailPageSpec,
  id: string | undefined,
  ctx: {
    readonly navigate?: Navigate;
    readonly fetch?: RosterFetch;
    readonly actionFetch?: ActionFetch;
    /**
     * CAOS-1912 — a component key to the element tag its bundle registers, for this page's
     * `pieces`. OPTIONAL, and absent is not a failure: a page with no pieces never asks, and a
     * caller that has no component metadata to answer from gets the written "not installed" line
     * rather than an unknown element drawn as an empty box.
     */
    readonly componentTag?: (key: string) => string | undefined;
  },
): { root: HTMLElement; done: Promise<void> } {
  const root = document.createElement('div');
  root.className = 'setup-pane';
  const loading = document.createElement('caos-empty-state');
  loading.setAttribute('title', 'Loading…');
  root.append(loading);

  const doFetch = ctx.fetch ?? defaultRosterFetch;
  const doActionFetch = ctx.actionFetch ?? defaultRosterActionFetch;
  // SINGLETON mode (no idParam) has no addressed id at all — never mind what the caller passed.
  const effectiveId = spec.idParam !== undefined ? id : undefined;

  const done = (async (): Promise<void> => {
    async function draw(): Promise<void> {
      const payload = await fetchRecord(spec, effectiveId, doFetch);
      if (payload === null) {
        if (spec.idParam !== undefined) {
          renderNotFound(root, spec, effectiveId ?? '', ctx.navigate);
        } else {
          renderLoadError(root, spec, ctx.navigate);
        }
        return;
      }
      await renderRecord(root, spec, effectiveId, payload, {
        navigate: ctx.navigate,
        fetch: doFetch,
        actionFetch: doActionFetch,
        redraw: () => void draw(),
        componentTag: ctx.componentTag,
      });
    }
    await draw();
  })();

  return { root, done };
}
